/**
 * Times render in the event session's own zone through Intl.DateTimeFormat (spec §3.7, K-6).
 * Eastern zones use the design's "ET" label; other zones use their short name.
 */
const fmt = (iso, tz, o) => new Intl.DateTimeFormat("en-US", { timeZone: tz, ...o }).format(new Date(iso));

/** The contract's label when set; else "ET" for any zone whose short name is EST/EDT at that instant; else the Intl short name. */
export function zoneLabel(iso, tz, label) {
  if (label) return label;
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso));
  const short = (parts || []).find((p) => p.type === "timeZoneName")?.value ?? tz;
  return /^E[SD]T$/.test(short) ? "ET" : short;
}

/** "Thu, Oct 22" */
export const fmtDay = (iso, tz) => fmt(iso, tz, { weekday: "short", month: "short", day: "numeric" });
/** "Thursday, October 22, 2026" */
export const fmtDayLong = (iso, tz) => fmt(iso, tz, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
/** Pieces for the type-coloured date tile: { weekday: "THU", day: "22", month: "OCT" } */
export const dateTile = (iso, tz) => ({
  weekday: fmt(iso, tz, { weekday: "short" }).toUpperCase(),
  day: fmt(iso, tz, { day: "numeric" }),
  month: fmt(iso, tz, { month: "short" }).toUpperCase(),
});

const t12 = (iso, tz) => fmt(iso, tz, { hour: "numeric", minute: "2-digit" });

/** "8:00 to 9:00 AM ET" (drops the first AM/PM when both match, per the design) */
export function fmtRange(s) {
  const a = t12(s.starts_at, s.timezone);
  const b = t12(s.ends_at, s.timezone);
  const start = a.slice(-2) === b.slice(-2) ? a.slice(0, -3) : a;
  return `${start} to ${b} ${zoneLabel(s.starts_at, s.timezone, s.timezone_label)}`;
}
/** "8:00 AM ET" */
export const fmtStart = (s) => `${t12(s.starts_at, s.timezone)} ${zoneLabel(s.starts_at, s.timezone, s.timezone_label)}`;

/** The hero's date line: "Thu, Oct 22 · 8:00 to 9:00 AM ET" */
export const fmtWhen = (s) => `${fmtDay(s.starts_at, s.timezone)} · ${fmtRange(s)}`;

/** Viewer's local range when it differs from the event session's zone. Browser only. */
export function localRange(s, viewerTz) {
  if (!viewerTz || viewerTz === s.timezone) return null;
  const a = t12(s.starts_at, s.timezone), b = t12(s.starts_at, viewerTz);
  if (a === b) return null;
  return `${b} to ${t12(s.ends_at, viewerTz)} ${zoneLabel(s.starts_at, viewerTz)}`;
}

/** ISO 8601 with the event session's UTC offset, for JSON-LD: 2027-03-10T18:00:00-08:00 (§4.5) */
export function isoWithOffset(iso, tz) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(d);
  const g = (t) => (parts || []).find((p) => p.type === t).value;
  const local = `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}:${g("second")}`;
  const asUtc = Date.UTC(+g("year"), +g("month") - 1, +g("day"), +g("hour"), +g("minute"), +g("second"));
  const offsetMin = Math.round((asUtc - d.getTime()) / 60000);
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  return `${local}${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}
